import React from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import SEO from "../components/SEO";
import SeoCTA from "../components/SeoCTA";
import { getSeoPageBySlug } from "../data/seoPages";

const SITE_URL = "https://www.subtitletsunami.com";

const blogContent = {
  "how-to-add-spanish-subtitles-to-video": {
    intro:
      "Adding Spanish subtitles to an English video can help more people understand, watch, and share your content. Instead of manually writing every subtitle line, SubtitleTsunami gives you a faster path from English video to Spanish-subtitled download.",
    sections: [
      {
        heading: "Step 1: Start with your English video",
        body:
          "Choose the video you want to subtitle. This could be a short-form clip, a social media video, an educational video, a client video, or a marketing video.",
      },
      {
        heading: "Step 2: Upload the video to SubtitleTsunami",
        body:
          "SubtitleTsunami is designed around a simple upload workflow. You upload the video, let the system process it, and wait for the finished subtitled version.",
      },
      {
        heading: "Step 3: Generate Spanish subtitles",
        body:
          "The goal is to create Spanish subtitles from the English content without forcing you to manually translate and time every line yourself.",
      },
      {
        heading: "Step 4: Download your subtitled video",
        body:
          "After processing, download your finished video with Spanish subtitles and use it wherever your audience watches your content.",
      },
    ],
    faqItems: [
      {
        question: "What is the fastest way to add Spanish subtitles to an English video?",
        answer:
          "The fastest way is to use a tool like SubtitleTsunami that lets you upload a video, generate Spanish subtitles, and download the finished subtitled video.",
      },
      {
        question: "Can I use Spanish subtitles for social media videos?",
        answer:
          "Yes. Spanish subtitles can be useful for TikTok, Instagram Reels, YouTube Shorts, and other social platforms.",
      },
    ],
  },

  "translate-youtube-shorts-to-spanish-subtitles": {
    intro:
      "YouTube Shorts move fast, and creators often need a quick way to reuse English videos for Spanish-speaking viewers. Spanish subtitles can make a Short easier to understand and more useful to a wider audience.",
    sections: [
      {
        heading: "Step 1: Pick the Short you want to translate",
        body:
          "Start with a short English video that already performs well or has a message worth sharing with Spanish-speaking viewers.",
      },
      {
        heading: "Step 2: Upload the Short",
        body:
          "Use SubtitleTsunami to upload the video and begin the subtitle generation process.",
      },
      {
        heading: "Step 3: Generate Spanish subtitles",
        body:
          "The system helps create Spanish subtitles so you do not have to handle the full translation and timing process manually.",
      },
      {
        heading: "Step 4: Download and publish",
        body:
          "Once the subtitled version is ready, download it and publish it as a Spanish-subtitled version of your original Short.",
      },
    ],
    faqItems: [
      {
        question: "Should I subtitle YouTube Shorts in Spanish?",
        answer:
          "If your content can help or entertain Spanish-speaking viewers, Spanish subtitles can make your Shorts easier to understand and reuse.",
      },
      {
        question: "Can I use the same video for English and Spanish audiences?",
        answer:
          "Yes. You can keep your original English video and create a Spanish-subtitled version for another audience.",
      },
    ],
  },

  "add-spanish-captions-to-tiktok-instagram-reels": {
    intro:
      "TikTok videos and Instagram Reels are often watched quickly, silently, or in noisy environments. Spanish captions can help Spanish-speaking viewers follow the message without needing a separate version of the video.",
    sections: [
      {
        heading: "Step 1: Choose your TikTok or Reel",
        body:
          "Start with a video that has clear spoken content and would make sense for a Spanish-speaking audience.",
      },
      {
        heading: "Step 2: Upload it to SubtitleTsunami",
        body:
          "Upload your short-form video and let SubtitleTsunami process it for Spanish subtitles.",
      },
      {
        heading: "Step 3: Generate captions",
        body:
          "SubtitleTsunami helps create Spanish subtitles for the video so you can avoid manually captioning every line.",
      },
      {
        heading: "Step 4: Download and post",
        body:
          "Download your Spanish-captioned video and post it to TikTok, Instagram Reels, or another short-form platform.",
      },
    ],
    faqItems: [
      {
        question: "Can Spanish captions help TikTok and Instagram videos?",
        answer:
          "Yes. Captions can make short-form videos easier to understand, especially when viewers watch without sound.",
      },
      {
        question: "Should I make a separate Spanish version of my video?",
        answer:
          "A Spanish-subtitled version can be a simple way to reuse your existing English video for Spanish-speaking viewers.",
      },
    ],
  },

  "why-spanish-subtitles-help-videos-reach-more-viewers": {
    intro:
      "Spanish subtitles can help English videos become easier to understand, easier to share, and more useful for Spanish-speaking viewers. For creators, this can turn one video into content that serves more than one audience.",
    sections: [
      {
        heading: "Spanish subtitles improve accessibility",
        body:
          "Subtitles make videos easier to follow for viewers who prefer reading along, watch without sound, or need extra language support.",
      },
      {
        heading: "Spanish subtitles help creators reuse content",
        body:
          "Instead of recording a completely new video, creators can create a Spanish-subtitled version of an existing English video.",
      },
      {
        heading: "Spanish subtitles can support organic reach",
        body:
          "When your content is easier for more people to understand, it has a better chance of being watched, shared, and reused across audiences.",
      },
      {
        heading: "SubtitleTsunami makes the workflow simpler",
        body:
          "SubtitleTsunami focuses on the practical workflow: upload your English video, generate Spanish subtitles, and download the finished video.",
      },
    ],
    faqItems: [
      {
        question: "Why add Spanish subtitles to English videos?",
        answer:
          "Spanish subtitles can help Spanish-speaking viewers understand and engage with videos that were originally created in English.",
      },
      {
        question: "Are Spanish subtitles useful for creators?",
        answer:
          "Yes. They can help creators reuse content for a broader audience without recording a completely separate video.",
      },
    ],
  },
};

const SeoBlogPage = () => {
  const location = useLocation();

  const slug = location.pathname
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");

  const page = getSeoPageBySlug(slug);
  const content = blogContent[slug];

  if (!page || !content) {
    return <Navigate to="/" replace />;
  }

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: page.title,
      description: page.metaDescription,
      datePublished: page.datePublished,
      dateModified: page.dateModified,
      author: {
        "@type": "Organization",
        name: "SubtitleTsunami",
      },
      publisher: {
        "@type": "Organization",
        name: "SubtitleTsunami",
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/BlueLogo.png`,
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${SITE_URL}${page.canonicalPath}`,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: content.faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    },
  ];

  return (
    <>
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        canonical={`${SITE_URL}${page.canonicalPath}`}
        image={`${SITE_URL}/og-image.png`}
        type="article"
        jsonLd={jsonLd}
      />

      <main className="min-h-screen bg-slate-950 px-4 py-12 text-white">
        <article className="mx-auto max-w-4xl">
          <section className="rounded-3xl border border-cyan-300/20 bg-slate-900/80 p-6 shadow-2xl shadow-cyan-950/30 md:p-10">
            <Link
              to="/english-to-spanish-subtitle-generator"
              className="mb-5 inline-flex text-sm font-bold text-cyan-300 hover:text-cyan-200"
            >
              ← English to Spanish Subtitle Generator
            </Link>

            <p className="mb-4 text-sm font-bold uppercase tracking-[0.3em] text-cyan-300">
              SubtitleTsunami Guide
            </p>

            <h1 className="mb-5 text-4xl font-black leading-tight text-white md:text-5xl">
              {page.h1}
            </h1>

            <p className="text-lg leading-8 text-slate-300">
              {content.intro}
            </p>
          </section>

          <SeoCTA
            title="Need Spanish subtitles fast?"
            description="Upload your video to SubtitleTsunami and generate English-to-Spanish subtitles."
            buttonText="Generate Spanish Subtitles"
          />

          <section className="mt-10 space-y-6">
            {content.sections.map((section) => (
              <div
                key={section.heading}
                className="rounded-3xl border border-white/10 bg-slate-900 p-6 md:p-8"
              >
                <h2 className="mb-3 text-2xl font-black text-white">
                  {section.heading}
                </h2>

                <p className="text-base leading-8 text-slate-300">
                  {section.body}
                </p>
              </div>
            ))}
          </section>

          <section className="mt-12 rounded-3xl border border-white/10 bg-slate-900 p-6 md:p-10">
            <h2 className="mb-6 text-3xl font-black text-white">
              Frequently asked questions
            </h2>

            <div className="space-y-5">
              {content.faqItems.map((item) => (
                <div
                  key={item.question}
                  className="rounded-2xl border border-white/10 bg-slate-950 p-5"
                >
                  <h3 className="mb-2 text-lg font-black text-white">
                    {item.question}
                  </h3>

                  <p className="leading-7 text-slate-300">
                    {item.answer}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-3xl border border-white/10 bg-slate-900 p-6 md:p-10">
            <h2 className="mb-4 text-2xl font-black text-white">
              Keep learning
            </h2>

            <div className="grid gap-4 md:grid-cols-2">
              <Link
                to="/english-to-spanish-subtitle-generator"
                className="rounded-2xl border border-white/10 bg-slate-950 p-5 text-slate-200 transition hover:border-cyan-300/40 hover:bg-slate-900"
              >
                English to Spanish Subtitle Generator
              </Link>

              <Link
                to="/how-to-add-spanish-subtitles-to-video"
                className="rounded-2xl border border-white/10 bg-slate-950 p-5 text-slate-200 transition hover:border-cyan-300/40 hover:bg-slate-900"
              >
                How to Add Spanish Subtitles to an English Video
              </Link>
            </div>
          </section>

          <SeoCTA
            title="Create your Spanish-subtitled video"
            description="Start with one upload and generate Spanish subtitles with SubtitleTsunami."
            buttonText="Start Free Upload"
          />
        </article>
      </main>
    </>
  );
};

export default SeoBlogPage;